import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  CarouselProvider,
  Slider,
  Slide,
  ImageWithZoom,
  ButtonBack,
  ButtonNext,
  Dot
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";

import usePageFromURL from "hooks/usePageFromURL.js";
import List from "@researchgate/react-intersection-list";
import { CSSTransition } from "react-transition-group";
import "assets/stylesheets/transition.css";
import "assets/stylesheets/agGridEditableHeader.css";
import { editableHeaderClass } from "utils";

import {
  isLoadingSelector,
  hasErrorsSelector,
  productsDataSelector
} from "selectors/productsSelector.js";
import {
  filtersReducerSelector,
  isDataLoadingSelector
} from "selectors/filtersSelector.js";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";
import { buyplanIdSelector } from "selectors/buyplanIdSelector.js";
import { currentUserDataSelector } from "selectors/currentUserSelector.js";

import { getProducts } from "actions/productsActions.js";
import { getFilters } from "actions/filterActions.js";
import { getBuyplanTotals } from "actions/buyplanTotalsActions.js";
import { startPendingSave, finishPendingSave } from "actions/pendingSaveActions.js";
import { apiBaseUrl } from "apis/apiHelpers.js";
import { addNotification } from "notificationStore.js";

import DefaultProductImage from "assets/images/default-product-image-128x160.jpg";
import Loader from "components/Loader/Loader.jsx";
import DiscountedPriceCell from "components/SparePart/DiscountedPriceCell.jsx";
import i18next from "i18next";

import {
  RANGE,
  PER_PAGE,
  BRANDSCOPE_ADMIN,
  AGENT_ADMIN,
  SUPER_SUPPLIER_ADMIN,
  SUPPLIER_ADMIN,
  SALES_STAFF,
  SALES_REP,
  ATS_STATUSES
} from "constants.js";

// ── Helpers ───────────────────────────────────────────────────────────────────
const extractPrice = priceField => {
  if (!priceField) return 0;
  if (typeof priceField === "object") {
    const values = Object.values(priceField);
    return values.length > 0 ? Number(values[0]) : 0;
  }
  return Number(priceField);
};

const formatFileSize = bytes => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdf = asset =>
  asset && (asset.url || "").toLowerCase().endsWith(".pdf");

const getPdfDownloadUrl = asset => {
  const base = apiBaseUrl || "";
  return `${base}/api/size_break_assets/${asset.id}/download`;
};

// Replaces the Cloudinary transformation segment with high-quality settings.
// Handles both /upload/{transformations}/v{version}/... and /upload/v{version}/... formats.
// Falls back to the original URL unchanged for non-Cloudinary URLs.
const getHighQualityImageUrl = url => {
  if (!url) return url;
  // Match Cloudinary upload URLs: .../image/upload/{optional-transforms}/v{version}/{path}
  const match = url.match(
    /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/(?:image|video)\/upload\/)(?:[^/]+\/)*(v\d+\/.+)$/
  );
  if (match) {
    return `${match[1]}f_auto,q_100,h_720,w_576/${match[2]}`;
  }
  // Non-Cloudinary: attempt simple dimension/quality param replacement as a best-effort
  return url
    .replace(/h_\d+/, "h_720")
    .replace(/w_\d+/, "w_576")
    .replace(/q_auto/g, "q_100");
};

// ── ATS helpers ───────────────────────────────────────────────────────────────

// ATS in the spare-part response is an object keyed by date e.g. { "2026-06-01": 10 }.
// When is_default_ats_zero is false and ats is empty/absent, treat as unlimited.
const getAtsTotal = (ats, isDefaultAtsZero) => {
  // Object format: { "2026-06-01": 10, ... }
  if (ats && typeof ats === "object" && !Array.isArray(ats)) {
    const values = Object.values(ats);
    if (values.length === 0) return isDefaultAtsZero ? 0 : Infinity;
    return values.reduce((sum, val) => sum + (isNaN(Number(val)) ? 0 : Number(val)), 0);
  }
  // Legacy array format: [[date, qty], ...] or [qty, ...]
  if (!Array.isArray(ats) || ats.length === 0) {
    return isDefaultAtsZero ? 0 : Infinity;
  }
  return ats.reduce((sum, entry) => {
    const val = Array.isArray(entry) ? Number(entry[1]) : Number(entry);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
};

const getAtsStatus = (ats, enteredQty, brand) => {
  const isDefaultAtsZero = brand ? brand.is_default_ats_zero : true;
  const lowQtyOffset =
    brand && brand.low_quantity_offset !== null && brand.low_quantity_offset !== undefined
      ? Number(brand.low_quantity_offset)
      : 5;
  const totalAts = getAtsTotal(ats, isDefaultAtsZero);
  if (totalAts === Infinity) return ATS_STATUSES.ok;
  if (totalAts === 0) return ATS_STATUSES.soldOut;
  const qty = Number(enteredQty) || 0;
  if (qty > 0 && qty > totalAts) return ATS_STATUSES.overQty;
  if (totalAts <= lowQtyOffset) return ATS_STATUSES.lowQty;
  return ATS_STATUSES.ok;
};

const atsStatusColor = status => {
  switch (status) {
    case ATS_STATUSES.soldOut:
    case ATS_STATUSES.overQty:
    case ATS_STATUSES.cumulativeOverQty:
      return "#dc3545";
    case ATS_STATUSES.lowQty:
      return "#e6a817";
    default:
      return "#28a745";
  }
};

// ── AG Grid cell renderers for the inner parts table ─────────────────────────

// Quantity cell: colour-coded by ATS status (same logic as qty_grid SizeBreakInput).
// When qty = 0 the ATS total is shown as a grayed-out placeholder hint.
const QuantityCellRenderer = ({ data }) => {
  const brand = data._brand;
  const qty =
    data.quantity !== null && data.quantity !== undefined
      ? Number(data.quantity)
      : 0;
  const status = getAtsStatus(data.ats, qty, brand);
  const isDefaultAtsZero = brand ? brand.is_default_ats_zero : true;
  const totalAts = getAtsTotal(data.ats, isDefaultAtsZero);
  const lowQtyOffset =
    brand && brand.low_quantity_offset !== null && brand.low_quantity_offset !== undefined
      ? Number(brand.low_quantity_offset)
      : 5;

  if (qty > 0) {
    // When ATS is not visible, skip colour-coding — just show the qty in black
    if (data.ats_visible === false) {
      return <span style={{ fontWeight: 600 }}>{qty}</span>;
    }
    return (
      <span style={{ color: atsStatusColor(status), fontWeight: 600 }}>
        {qty}
      </span>
    );
  }

  // qty === 0: only show ATS hint if ats_visible is not explicitly false
  if (data.ats_visible === false) {
    return null;
  }

  // For spare parts, don't show infinity symbol placeholder
  if (totalAts === Infinity) {
    return null;
  }
  if (totalAts > 0) {
    const hintColor = totalAts <= lowQtyOffset ? "#e6a817" : "#bbb";
    return (
      <span style={{ color: hintColor }}>{totalAts}</span>
    );
  }
  // totalAts === 0 → sold out
  return (
    <span style={{ color: "#dc3545", fontSize: "0.75em", fontWeight: 600 }}>
      Sold Out
    </span>
  );
};


const WsPriceCellRenderer = ({ data }) => {
  const isPbDiscounted =
    data.pb_discounted_wholesale_price !== null &&
    data.pb_discounted_wholesale_price !== undefined;

  return (
    <DiscountedPriceCell
      originalPrice={`$${Number(extractPrice(data.wholesale_price)).toFixed(2)}`}
      discountedPrice={data.discounted_display_wholesale_price}
      isDiscounted={data.is_discounted}
      isPbDiscounted={isPbDiscounted}
    />
  );
};
WsPriceCellRenderer.displayName = "WsPriceCellRenderer";

// ── Product Details Panel (image carousel + AG Grid parts table) ──────────────
const ProductDetailsPanel = ({
  details,
  isLoading,
  buyplanId,
  warehouseId,
  dispatch,
  isAdminUser,
  isSalesStaff,
  requestTotalsRefresh
}) => {
  const [rowData, setRowData] = useState([]);
  // Tail promise of the persist queue for each row (keyed by product_part_relationship_id).
  // Edits to different rows run concurrently; edits to the same row are serialized so
  // fast typing/tabbing never gets silently dropped.
  const rowQueues = useRef(new Map());
  // Caches the bpsb_id returned by add_item so repeated edits on an already-added
  // row/field only call update_quantity, instead of re-POSTing to items.json (and
  // paying its CORS preflight) on every keystroke commit. Keyed by
  // `${productPartRelationshipId}:${field}` since quantity and backorder_quantity
  // resolve to different BuyplanSizeBreaks for the same part.
  const bpsbIdCache = useRef(new Map());

  const assets = useMemo(() => {
    if (!details) return [];
    const d = details.data || details;
    const rawAssets =
      d.parent_product &&
      Array.isArray(d.parent_product.assets) &&
      d.parent_product.assets.length > 0
        ? d.parent_product.assets
        : null;

    if (rawAssets) {
      return [
        ...rawAssets.filter(a => a.is_primary),
        ...rawAssets.filter(a => !a.is_primary)
      ];
    }
    const parentImg = d.parent_product && d.parent_product.primary_image;
    if (parentImg) return [{ id: "primary", url: parentImg, file_name: "", is_primary: true }];
    return [];
  }, [details]);

  const productName = useMemo(() => {
    if (!details) return "";
    const d = details.data || details;
    return (d.parent_product && d.parent_product.name) || "";
  }, [details]);

  // Backorder column is shown only when both brand AND release allow backorders
  const isBackorderAllowed = useMemo(() => {
    if (!details) return false;
    const d = details.data || details;
    const brandAllows = d.brand && d.brand.is_backorder_allowed === true;
    const releaseAllows = d.release && d.release.is_backorder_allowed === true;
    return brandAllows && releaseAllows;
  }, [details]);

  // Build rowData for AG Grid from parts; attach brand config to every row so
  // cell renderers can access it without extra context plumbing.
  useEffect(() => {
    if (!details) { setRowData([]); return; }
    const d = details.data || details;
    const parts = d.spare_parts || d.parts || d.items || d.product_parts || [];
    const brandConfig = d.brand || null;
    setRowData(parts.map(p => ({ ...p, _brand: brandConfig })));
  }, [details]);

  const findBpsbId = obj => {
    if (!obj || typeof obj !== "object") return null;
    if (obj.bpsb_id) return obj.bpsb_id;
    if (obj.buyplan_size_break_id) return obj.buyplan_size_break_id;
    if (Array.isArray(obj)) {
      for (const item of obj) { const f = findBpsbId(item); if (f) return f; }
    } else {
      for (const key of Object.keys(obj)) { const f = findBpsbId(obj[key]); if (f) return f; }
    }
    return null;
  };

  // Persists a single quantity/backorder_quantity edit. Called from the per-row
  // queue in onCellValueChanged so overlapping edits to the same row never race.
  const persistQuantity = async (params, field, parsedNew, parsedOld, productPartRelationshipId) => {
    const authToken = sessionStorage.getItem("authToken");
    const baseUrl = apiBaseUrl || "";
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/brandscope.com; version=1",
      Authorization: authToken ? `Bearer ${authToken}` : "",
      warehouse_id: warehouseId || ""
    };

    const cacheKey = `${productPartRelationshipId}:${field}`;
    // quantity and backorder_quantity are different BuyplanSizeBreaks — the API
    // exposes both ids directly on the part, so prefer those over discovering
    // one via add_item. Using the wrong id here is what let a backorder edit
    // overwrite the regular quantity (and vice versa).
    const idField = field === "backorder_quantity" ? "backorder_bsb_id" : "buyplan_size_break_id";

    // Tracked so checkout can wait for this save to actually land before
    // submitting an order/payment total built from data that doesn't include it.
    if (dispatch) dispatch(startPendingSave());

    try {
      // ── 2. Resolve the bpsb_id for this row/field. ───────────────────────
      //    Prefer the id already on the row, then the cache from an earlier
      //    edit in this session, so a quantity change on an already-added part
      //    goes straight to update_quantity instead of re-POSTing to items.json.
      let bpsbId = params.data[idField] || bpsbIdCache.current.get(cacheKey);

      if (!bpsbId) {
        // add_item is idempotent — also retrieves bpsb_id. For backorder_quantity,
        // pass is_pre_order: true so the backend finds/creates the pre-order
        // BuyplanSizeBreak and returns its bpsb_id.
        const addItemBody = {
          product_part_relationship_id: productPartRelationshipId,
          warehouse_id: warehouseId || ""
        };
        if (field === "backorder_quantity") {
          addItemBody.is_pre_order = true;
        }

        const addRes = await fetch(`${baseUrl}/api/buyplans/${buyplanId}/items.json`, {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify(addItemBody)
        });
        let addData = {};
        try { addData = await addRes.json(); } catch (_) { /* ignore */ }
        if (!addRes.ok) {
          const errText = (addData.errors && addData.errors[0]) || addData.error || "";
          const alreadyExists =
            addRes.status === 422 ||
            addRes.status === 409 ||
            (typeof errText === "string" && /already|taken|exist/i.test(errText));
          if (!alreadyExists) throw new Error(errText || `add_item failed: ${addRes.status}`);
        }

        bpsbId = findBpsbId(addData);
        if (!bpsbId) {
          // eslint-disable-next-line no-console
          console.error("[SparePart] add_item response (bpsb_id missing):", addData);
          throw new Error("Missing bpsb_id in add_item response");
        }
      }
      bpsbIdCache.current.set(cacheKey, bpsbId);

      // ── 3. Persist the quantity on the server. ───────────────────────────
      const updRes = await fetch(
        `${baseUrl}/api/buyplans/${buyplanId}/size_breaks/${bpsbId}/update_quantity.json`,
        {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify({ quantity: String(parsedNew), wholesale_price: "", warehouse_id: warehouseId || "" })
        }
      );
      if (!updRes.ok) {
        // The cached bpsb_id may be stale (e.g. the size break was removed
        // server-side) — drop it so the next edit re-resolves it via add_item.
        if (updRes.status === 404) bpsbIdCache.current.delete(cacheKey);
        let updData = {};
        try { updData = await updRes.json(); } catch (_) { /* ignore */ }
        const errText =
          (Array.isArray(updData.errors) && updData.errors[0]) ||
          updData.error ||
          `update_quantity failed: ${updRes.status}`;
        throw new Error(errText);
      }

      // ── 4. Refresh buyplan totals in header (no full row re-fetch needed
      //    because the optimistic update already shows the correct value).
      //    Debounced (see requestTotalsRefresh) — the shared GET_DATA saga
      //    dedupes identical in-flight requests, so firing one per edit during
      //    fast multi-row entry silently drops all but the first. ───
      if (requestTotalsRefresh) {
        requestTotalsRefresh();
      } else if (dispatch) {
        dispatch(getBuyplanTotals(buyplanId));
      }
    } catch (err) {
      addNotification({
        type: "error",
        message: err && err.message ? err.message : "Failed to update quantity"
      });
      // ── Revert the optimistic update on failure ───────────────────────────
      setRowData(prev =>
        prev.map(p =>
          String(p.product_part_relationship_id) === String(productPartRelationshipId)
            ? { ...p, [field]: parsedOld }
            : p
        )
      );
      params.node.setDataValue(field, parsedOld, "revert");
    } finally {
      if (dispatch) dispatch(finishPendingSave());
    }
  };

  // Called when any editable cell value changes in AG Grid.
  // Uses an optimistic update approach — rowData is updated immediately so the
  // UI (including the computed Subtotal column) reflects the change without
  // waiting for the API round-trip. On failure the optimistic value is reverted.
  //
  // The actual persist is queued per row (see rowQueues above) rather than guarded
  // by a single global in-flight flag: a global flag silently dropped any edit that
  // landed while another row's request was still in flight, which is exactly what
  // happens when a retailer tabs quickly through many quantity fields — the value
  // never reached the server and the part never showed up in the buyplan.
  const onCellValueChanged = params => {
    // Ignore the change event fired by persistQuantity's own revert-on-failure
    // (params.node.setDataValue below) — otherwise the revert re-enters this
    // handler, fails the same way, reverts again, and loops forever.
    if (params.source === "revert") return;

    const field = params.colDef.field;
    if (field !== "quantity" && field !== "backorder_quantity") return;
    if (params.oldValue === params.newValue) return;

    const part = params.data;
    const parsedNew = isNaN(Number(params.newValue)) ? 0 : Math.max(0, Number(params.newValue));
    const parsedOld = isNaN(Number(params.oldValue)) ? 0 : Number(params.oldValue);
    if (parsedNew === parsedOld) return;

    const productPartRelationshipId = part.product_part_relationship_id;
    if (!buyplanId || !productPartRelationshipId) return;

    // ── 1. Optimistic update: reflect the new value in rowData immediately ───
    setRowData(prev =>
      prev.map(p =>
        String(p.product_part_relationship_id) === String(productPartRelationshipId)
          ? { ...p, [field]: parsedNew }
          : p
      )
    );

    // ── 2. Queue the persist behind any request already in flight for this row ──
    const rowKey = String(productPartRelationshipId);
    const previousInQueue = rowQueues.current.get(rowKey) || Promise.resolve();
    const task = previousInQueue.catch(() => undefined).then(() =>
      persistQuantity(params, field, parsedNew, parsedOld, productPartRelationshipId)
    );
    rowQueues.current.set(rowKey, task);
  };

  // Quantity is the only column retailers actually type into, so Tab should move
  // down to the next row's quantity cell (fast vertical entry) instead of ag-grid's
  // default horizontal move to the next column.
  const tabToNextCell = useCallback(params => {
    const { previousCellPosition, backwards, api } = params;
    const nextRowIndex = previousCellPosition.rowIndex + (backwards ? -1 : 1);
    if (nextRowIndex < 0 || nextRowIndex >= api.getDisplayedRowCount()) return null;
    return {
      rowIndex: nextRowIndex,
      column: previousCellPosition.column,
      rowPinned: previousCellPosition.rowPinned
    };
  }, []);

  // Role part of the Quantity/Backorder rules, kept out of the per-row
  // functions so the header pencil is hidden for admin and sales staff.
  const canEditQuantities = !isAdminUser && !isSalesStaff;

  // Column definitions — same pattern as BuyplanDetailsContainer
  const columnDefs = useMemo(() => [
    {
      field: "schematic_id",
      headerName: i18next.t("sparePart.grid.columns.schematicId", "Schematic ID"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "part_name",
      headerName: i18next.t("sparePart.grid.columns.partDescription", "Part Description"),
      flex: 2,
      minWidth: 160,
      cellClass: "part-description-cell",
      tooltipField: "part_name",
    },
    {
      field: "part_sku",
      headerName: i18next.t("sparePart.grid.columns.partSku", "Part SKU"),
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: i18next.t("sparePart.grid.columns.wsPrice", "W/S Price"),
      flex: 1,
      minWidth: 130,
      // valueGetter returns the effective price so sorting works correctly
      valueGetter: p => {
        if (
          p.data.is_discounted &&
          p.data.pb_discounted_wholesale_price !== null &&
          p.data.pb_discounted_wholesale_price !== undefined
        ) {
          return Number(p.data.pb_discounted_wholesale_price);
        }
        return extractPrice(p.data.wholesale_price);
      },
      cellRenderer: WsPriceCellRenderer,
    },
    {
      headerName: i18next.t("sparePart.grid.columns.rrPrice", "R/R Price"),
      flex: 1,
      minWidth: 90,
      valueGetter: p => extractPrice(p.data.retail_price),
      valueFormatter: p =>
        p.value !== null && p.value !== undefined
          ? `$${Number(p.value).toFixed(2)}`
          : "",
    },
    {
      field: "quantity",
      headerName: i18next.t("sparePart.grid.columns.quantity", "Quantity"),
      // When ats_visible is false, bypass all ATS restrictions (always editable, no sold-out lock)
      editable: canEditQuantities && (p =>
        p.data.ats_visible === false ||
          getAtsStatus(p.data.ats, p.data.quantity, p.data._brand) !== ATS_STATUSES.soldOut),
      cellStyle: p => {
        if (isAdminUser || isSalesStaff) return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
        const qty = p.data.quantity !== null && p.data.quantity !== undefined ? Number(p.data.quantity) : 0;
        if (p.data.ats_visible !== false) {
          const isSoldOut = getAtsStatus(p.data.ats, p.data.quantity, p.data._brand) === ATS_STATUSES.soldOut;
          if (isSoldOut) return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
          if (qty === 0) return { background: "#f5f5f5" };
        }
        return {};
      },
      flex: 1,
      minWidth: 100,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: { min: 0, precision: 0, maxLength: 5 },
      // Show ATS hint in gray / colour-coded when qty = 0 (qty_grid pattern)
      cellRenderer: QuantityCellRenderer,
      valueGetter: p =>
        p.data.quantity !== null && p.data.quantity !== undefined
          ? p.data.quantity
          : 0,
      // valueSetter is required so ag-grid commits the edit and fires onCellValueChanged
      valueSetter: p => {
        p.data.quantity = p.newValue;
        return true;
      },
      valueParser: p => {
        const v = Number(p.newValue);
        if (isNaN(v)) return 0;
        // Reset to 0 if value exceeds 5 digits
        const maxValue = 99999;
        if (v > maxValue) {
          addNotification({
            type: "error",
            message: "Please enter Qtys between 0 - 99999"
          });
          return 0;
        }
        return v;
      },
    },
    ...(isBackorderAllowed ? [{
      field: "backorder_quantity",
      headerName: i18next.t("sparePart.grid.columns.backorderQuantity", "Backorder Quantity"),
      // Only populated for parts with a lead time at all (has_lead_time); among those,
      // editable only when a positive pre_order_lead_time_days is configured, and
      // never for admin or sales staff users.
      editable: canEditQuantities && (p =>
        !!p.data.has_lead_time &&
        Number(p.data.pre_order_lead_time_days) > 0),
      cellStyle: p => {
        if (!p.data.has_lead_time) return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
        if (isAdminUser || isSalesStaff || !(Number(p.data.pre_order_lead_time_days) > 0)) {
          return { background: "#f5f5f5", color: "#ccc", cursor: "not-allowed" };
        }
        return {};
      },
      flex: 1,
      minWidth: 140,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: { min: 0, precision: 0 },
      valueGetter: p => {
        if (!p.data.has_lead_time) return "";
        const qty = p.data.backorder_quantity;
        return qty !== null && qty !== undefined && Number(qty) !== 0 ? qty : "∞";
      },
      valueParser: p => {
        const v = Number(p.newValue);
        return isNaN(v) ? 0 : v;
      },
    }] : []),
    {
      headerName: i18next.t("sparePart.grid.columns.subtotal", "Subtotal"),
      flex: 1,
      minWidth: 90,
      valueGetter: p => {
        // Use effective_wholesale_price when discounted, otherwise regular wholesale_price
        const effectivePrice =
          p.data.is_discounted && p.data.effective_wholesale_price
            ? Number(p.data.effective_wholesale_price)
            : extractPrice(p.data.wholesale_price);
        const qty =
          p.data.quantity !== null && p.data.quantity !== undefined
            ? Number(p.data.quantity)
            : 0;
        return `$${(effectivePrice * qty).toFixed(2)}`;
      },
    },
  ], [isBackorderAllowed, isAdminUser, isSalesStaff, canEditQuantities]);


  // {
  //     headerName: i18next.t("sparePart.grid.columns.subtotal", "Subtotal"),
  //     flex: 1,
  //     minWidth: 90,
  //     valueGetter: p => {
  //       // Use PB price for subtotal when a price book discount applies
  //       const effectivePrice =
  //         p.data.is_discounted &&
  //         p.data.pb_discounted_wholesale_price !== null &&
  //         p.data.pb_discounted_wholesale_price !== undefined
  //           ? Number(p.data.pb_discounted_wholesale_price)
  //           : extractPrice(p.data.wholesale_price);
  //       const qty =
  //         p.data.quantity !== null && p.data.quantity !== undefined
  //           ? Number(p.data.quantity)
  //           : 0;
  //       return `$${(effectivePrice * qty).toFixed(2)}`;
  //     },
  //   },
  // ], [isBackorderAllowed]);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: editableHeaderClass,
  }), []);

  const totalAssets = assets.length;

  const renderSlideContent = asset => {
    if (isPdf(asset)) {
      return (
        <div className="pdf-card">
          <i className="fa fa-file-text-o pdf-icon" />
          <div className="pdf-filename">{asset.file_name}</div>
          {asset.file_size && <div className="pdf-size">Size: {formatFileSize(asset.file_size)}</div>}
          <a
            href={getPdfDownloadUrl(asset)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-sm pdf-btn"
            onClick={e => e.stopPropagation()}
          >
            <i className="fa fa-eye" /> View PDF
          </a>
        </div>
      );
    }
    return (
      <ImageWithZoom
        src={getHighQualityImageUrl(asset.large_url || asset.url)}
        alt={asset.file_name || productName}
        onError={e => { e.target.src = DefaultProductImage; }}
      />
    );
  };

  if (isLoading) return <Loader />;
  if (!details) return null;

  return (
    <StyledDetailsPanel>
      {/* Asset carousel — mirrors the Buy Plan quick-view image carousel's zoom UX */}
      <div className="image-carousel">
        {totalAssets > 0 ? (
          <CarouselProvider
            naturalSlideWidth={480}
            naturalSlideHeight={400}
            totalSlides={totalAssets}
          >
            <div className="carousel-content">
              <ButtonBack className="carousel-btn prev">&#8249;</ButtonBack>
              <Slider>
                {assets.map((asset, index) => (
                  <Slide key={asset.id || index} index={index}>
                    {renderSlideContent(asset)}
                  </Slide>
                ))}
              </Slider>
              <ButtonNext className="carousel-btn next">&#8250;</ButtonNext>
            </div>

            {totalAssets > 1 && (
              <section className="carousel-navigation-actions">
                {assets.map((asset, index) => (
                  <Dot key={asset.id || index} slide={index} className="carousel-dot">
                    {isPdf(asset) ? (
                      <i className="fa fa-file-text-o" />
                    ) : (
                      <img
                        src={getHighQualityImageUrl(asset.url)}
                        alt=""
                        onError={e => { e.target.src = DefaultProductImage; }}
                      />
                    )}
                  </Dot>
                ))}
              </section>
            )}
          </CarouselProvider>
        ) : (
          <img
            src={DefaultProductImage}
            alt="No sample available"
            className="carousel-image no-sample"
          />
        )}
      </div>

      {/* Parts AG Grid — same pattern as BuyplanDetailsContainer */}
      {rowData.length > 0 && (
        <div className="grid-wrapper">
          <div className="ag-theme-alpine spare-parts-detail-grid">
            <AgGridReact
              rowData={rowData}
              getRowId={params => String(params.data.product_part_relationship_id)}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onCellValueChanged={onCellValueChanged}
              tabToNextCell={tabToNextCell}
              animateRows={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              rowHeight={35}
              headerHeight={38}
              domLayout="autoHeight"
            />
          </div>
          <div className="grid-footer">
            <span className="total-rows-label">
              {i18next.t("sparePart.grid.footer.totalItems", "Total Items")}{" "}
              <strong>{rowData.length}</strong>
            </span>
          </div>
        </div>
      )}

      {rowData.length === 0 && (
        <div className="no-data">
          {i18next.t("sparePart.noPartsAvailable", "No parts available.")}
        </div>
      )}
    </StyledDetailsPanel>
  );
};

// ── Main container ────────────────────────────────────────────────────────────

let prevOffset = 0;

const SparePartProductsListContainer = () => {
  const { releaseId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const buyPlanState = queryParams.get("type");
  const page = usePageFromURL();

  const dispatch = useDispatch();

  const isLoading = useSelector(isLoadingSelector);
  const hasErrors = useSelector(hasErrorsSelector);
  const data = useSelector(productsDataSelector);
  const buyplanId = useSelector(buyplanIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);
  const currentUser = useSelector(currentUserDataSelector);

  const isAdminUser = currentUser.get("role") === BRANDSCOPE_ADMIN;
  const isAgentAdmin = currentUser && currentUser.get("role") === AGENT_ADMIN;
  const isSalesRep = currentUser && currentUser.get("role") === SALES_REP;
  const isSalesStaff = currentUser && currentUser.get("role") === SALES_STAFF;
  const isSupplierAdmin = currentUser && currentUser.get("role") === SUPPLIER_ADMIN;
  const isSuperSupplierAdmin = currentUser && currentUser.get("role") === SUPER_SUPPLIER_ADMIN;

  const filters = useSelector(filtersReducerSelector);
  const appliedFilters = filters.get("appliedFilters");
  const isDataLoading = useSelector(isDataLoadingSelector);

  // Expanded-row state
  // - expandedIndices: Set of row indices that are currently open (multiple can be open at once)
  // - detailsMap: { [rowIndex]: detailsObject } — cached details per row
  // - loadingMap: { [rowIndex]: boolean } — loading state per row
  const [expandedIndices, setExpandedIndices] = useState(new Set());
  const [detailsMap, setDetailsMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});

  const handleProductClick = async (product, rowIndex) => {
    // Toggle: close if the same row is already open
    if (expandedIndices.has(rowIndex)) {
      setExpandedIndices(prev => {
        const next = new Set(prev);
        next.delete(rowIndex);
        return next;
      });
      return;
    }

    const rawIntegrationId = product.integration_id;
    const integrationId = Array.isArray(rawIntegrationId)
      ? rawIntegrationId[0]
      : rawIntegrationId;
    const brandId = product.brand_id;

    // Open this row and start loading its details (keep all other rows open)
    setExpandedIndices(prev => new Set([...prev, rowIndex]));
    setLoadingMap(prev => ({ ...prev, [rowIndex]: true }));

    try {
      const authToken = sessionStorage.getItem("authToken");
      const qs = buyplanId
        ? `?buyplan_id=${buyplanId}&warehouse_id=${warehouseId || ""}`
        : "";
      const baseUrl = apiBaseUrl || "";
      const res = await fetch(
        `${baseUrl}/api/brands/${brandId}/products/${integrationId}${qs}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/brandscope.com; version=1",
            Authorization: authToken ? `Bearer ${authToken}` : ""
          }
        }
      );
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const details = await res.json();
      setDetailsMap(prev => ({ ...prev, [rowIndex]: { details, brandId, integrationId } }));
    } catch (_err) {
      // silent
    } finally {
      setLoadingMap(prev => ({ ...prev, [rowIndex]: false }));
    }
  };

  const totalProductsCount = data.get("count");
  const loadedProducts = data.get("pr");
  const loadedProductsCount = loadedProducts ? loadedProducts.size : 0;
  const awaitingMoreProducts = () => data.get("mostRecentPrSize") === PER_PAGE;

  const onIntersection = () => {
    if (awaitingMoreProducts()) {
      const newOffset = prevOffset + PER_PAGE;
      dispatch(
        getProducts(
          releaseId, PER_PAGE, newOffset, appliedFilters, false, buyPlanState, buyplanId, warehouseId, { page }
        )
      );
      prevOffset = newOffset;
    }
  };

  const itemsRenderer = (items, ref) => <div ref={ref}>{items}</div>;

  const itemRenderer = index => {
    const raw = data.getIn(["pr", index]);
    if (!raw) return null;
    // Normalise: handle both Immutable Map and plain JS object
    const product = raw && typeof raw.toJS === "function" ? raw.toJS() : raw;
    const productId = product.product_id;
    // Use the list index as the unique expansion key so that separate size-break
    // rows for the same product each expand/collapse independently.
    const isExpanded = expandedIndices.has(index);
    const rowEntry = detailsMap[index];

    return (
      <div key={`${productId}-${index}`}>
        <StyledProductRow
          isExpanded={isExpanded}
          onClick={() => handleProductClick(product, index)}
        >
          <span className="product-name">{product.product_name}</span>
          {product.size && (
            <span className="product-size">{product.size}</span>
          )}
          <img
            className="product-thumb"
            src={product.product_primary_image || DefaultProductImage}
            alt={product.product_name}
            onError={e => { e.target.src = DefaultProductImage; }}
          />
          <span className={`product-chevron${isExpanded ? " expanded" : ""}`}>
            &#8250;
          </span>
        </StyledProductRow>

        {isExpanded && (
          <StyledProductDetails>
            <ProductDetailsPanel
              details={rowEntry ? rowEntry.details : null}
              isLoading={!!loadingMap[index]}
              buyplanId={buyplanId}
              warehouseId={warehouseId}
              brandId={rowEntry ? rowEntry.brandId : null}
              integrationId={rowEntry ? rowEntry.integrationId : null}
              dispatch={dispatch}
              isAdminUser={isAdminUser}
              isSalesStaff={isSalesStaff}
              requestTotalsRefresh={requestTotalsRefresh}
            />
          </StyledProductDetails>
        )}
      </div>
    );
  };

  const getProductsListLoader = () => <Loader />;

  useEffect(() => {
    if (
      buyplanId ||
      isAdminUser ||
      isAgentAdmin ||
      isSuperSupplierAdmin ||
      isSalesRep ||
      isSalesStaff ||
      isSupplierAdmin
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      dispatch(getFilters(releaseId, RANGE, buyplanId, code));
    }
  }, [
    dispatch,
    releaseId,
    buyplanId,
    isAdminUser,
    isAgentAdmin,
    isSuperSupplierAdmin,
    isSalesRep,
    isSalesStaff,
    isSupplierAdmin,
    page
  ]);

  useEffect(() => {
    prevOffset = 0;
    if (isDataLoading === false) {
      dispatch(
        getProducts(
          releaseId, PER_PAGE, 0, appliedFilters, true, buyPlanState, buyplanId, warehouseId, { page }
        )
      );
    }
  }, [dispatch, isDataLoading, releaseId, appliedFilters, buyPlanState, buyplanId, warehouseId, page]);

  // Keep buyplan totals fresh for the order-review summary
  useEffect(() => {
    if (buyplanId) {
      dispatch(getBuyplanTotals(buyplanId));
    }
  }, [dispatch, buyplanId]);

  // Debounce totals refreshes triggered by quantity edits. The GET_DATA saga
  // dedupes identical in-flight requests (same path+params), and every
  // getBuyplanTotals(buyplanId) call looks identical — so firing one per edit
  // while tabbing quickly through several rows means all but the first get
  // silently dropped, leaving the header stuck on a stale (sometimes zero)
  // total. Coalescing rapid edits into a single trailing request avoids that.
  const totalsRefreshTimer = useRef(null);
  const requestTotalsRefresh = useCallback(() => {
    if (!buyplanId) return;
    clearTimeout(totalsRefreshTimer.current);
    totalsRefreshTimer.current = setTimeout(() => {
      dispatch(getBuyplanTotals(buyplanId));
    }, 600);
  }, [dispatch, buyplanId]);

  useEffect(() => () => clearTimeout(totalsRefreshTimer.current), []);

  return (
    <>
      {isLoading && getProductsListLoader()}

      {hasErrors && <div className="error-msg">{data.get("message")}</div>}

      {!isLoading && totalProductsCount === 0 && (
        <div className="d-flex justify-content-center align-items-center h-100">
          {i18next.t("dnd.messages.noProductsFound")}
        </div>
      )}

      <CSSTransition
        classNames="fade"
        timeout={1200}
        in={!isLoading && !hasErrors && totalProductsCount > 0}
        unmountOnExit
      >
        <>
          <List
            pageSize={PER_PAGE}
            itemCount={loadedProductsCount}
            awaitMore={awaitingMoreProducts()}
            threshold="50%"
            itemsRenderer={itemsRenderer}
            renderItem={itemRenderer}
            onIntersection={onIntersection}
          />

          {awaitingMoreProducts() && getProductsListLoader()}
        </>
      </CSSTransition>
    </>
  );
};

// ── Styled components ─────────────────────────────────────────────────────────

const StyledProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #dddddd;
  border-radius: ${({ isExpanded }) => (isExpanded ? "10px 10px 0 0" : "10px")};
  margin-bottom: ${({ isExpanded }) => (isExpanded ? "0" : "8px")};
  background: #f9f9f9;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f0f0f0;
  }

  /* product name — left-aligned, shrinks to content */
  .product-name {
    font-size: 16px;
    font-weight: 500;
    color: var(--bs-dark-black, #333);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
  }

  /* size — secondary identifier, shrinks to content */
  .product-size {
    font-size: 13px;
    font-weight: 600;
    color: #555;
    white-space: nowrap;
    flex-shrink: 0;
    background: #e9ecef;
    border-radius: 4px;
    padding: 2px 8px;
  }

  /* thumbnail — to the right of integration id */
  .product-thumb {
    width: 36px;
    height: 36px;
    object-fit: contain;
    flex-shrink: 0;
  }

  /* chevron — far right */
  .product-chevron {
    font-size: 28px;
    font-weight: 700;
    color: #555;
    line-height: 1;
    flex-shrink: 0;
    margin-left: 4px;
    display: inline-block;
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }
`;

const StyledProductDetails = styled.div`
  background: #fff;
  border: 1px solid #dddddd;
  border-top: none;
  border-radius: 0 0 10px 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  overflow-x: auto;
`;

const StyledDetailsPanel = styled.div`
  .image-carousel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #eee;
    margin-bottom: 12px;

    .carousel-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }

    .carousel__slider {
      width: 480px;
      height: 400px;
    }

    .carousel__inner-slide {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .carousel__image,
    .carousel__image--with-background {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background-size: contain !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
    }

    .carousel__zoom-image-overlay {
      cursor: zoom-in;
    }

    .carousel-image.no-sample {
      width: 480px;
      height: 400px;
      object-fit: contain;
      opacity: 0.4;
    }

    .pdf-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      border: 2px dashed #ccc;
      border-radius: 14px;
      padding: 32px 40px;
      width: 100%;
      height: 75%;
      background: #fafafa;
      box-sizing: border-box;

      .pdf-icon { font-size: 48px; color: #888; }
      .pdf-filename { font-size: 14px; font-weight: 500; color: #333; text-align: center; word-break: break-word; max-width: 240px; }
      .pdf-size { font-size: 12px; color: #888; }
      .pdf-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 4px; }
    }

    .carousel-btn {
      background: none;
      border: none;
      font-size: 60px;
      color: #555;
      cursor: pointer;
      line-height: 1;
      padding: 0 4px;
      transition: color 0.15s;
      flex-shrink: 0;

      &:disabled { color: #ccc; cursor: default; }
      &:hover:not(:disabled) { color: #222; }
    }

    .carousel-navigation-actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 6px;
    }

    .carousel-dot {
      padding: 0;
      border: solid 1.5px #e5e5e5;
      border-radius: 3px;
      overflow: hidden;
      cursor: pointer;
      background: none;

      &.carousel__dot--selected {
        border-color: #555;
      }

      img {
        display: block;
        width: 34px;
        height: 45px;
        object-fit: contain;
      }

      .fa-file-text-o {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 45px;
        color: #888;
      }
    }
  }

  .grid-wrapper {
    margin-top: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
  }

  .spare-parts-detail-grid {
    width: 100%;

    .ag-cell {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* text-overflow has no effect on a flex container's own text, so this
       column reverts to a block box (ag-grid's default) to get real ellipsis */
    .ag-cell.part-description-cell {
      display: block;
    }
  }

  .grid-footer {
    background: linear-gradient(to top, #f9fafb 0%, #ffffff 100%);
    border-top: 1px solid #e5e7eb;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .total-rows-label {
      font-size: 0.8125rem;
      color: #6b7280;
      font-weight: 500;

      strong { font-weight: 700; color: #1f2937; margin-left: 4px; }
    }
  }

  .no-data {
    text-align: center;
    padding: 20px;
    color: #888;
    font-size: 0.875rem;
  }
`;

export default React.memo(SparePartProductsListContainer);